import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { mockNfaLfaService } from '@/services/mock/MockNfaLfaService';
import { mockPurchaseRequestService } from '@/services/mock/MockPurchaseRequestService';
import { mockDocumentCommentService } from '@/services/mock/MockDocumentCommentService';
import { NoteForApproval, PurchaseRequest, DocumentAttachment } from '@/types/domain';
import { StatusBadge } from '@/components/data-display/StatusBadge';
import { ApprovalStatusHeader } from '@/components/workflow/ApprovalStatusHeader';
import { SlaTrackerWidget } from '@/components/workflow/SlaTrackerWidget';
import { ApprovalActionBar } from '@/components/workflow/ApprovalActionBar';
import {
  ApproveModal,
  RejectModal,
  SendBackModal,
  ClarificationModal,
  DelegateModal,
} from '@/components/workflow/WorkflowModals';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import {
  ArrowLeft,
  Building2,
  Tag,
  ShieldCheck,
  TrendingUp,
  FileText,
  Download,
  CheckCircle2,
  Calendar,
  Layers,
} from 'lucide-react';
import { ROUTES } from '@/constants/routes';
import { unifiedLifecycleCoordinator } from '@/services/mock/UnifiedLifecycleCoordinator';

const formatInr = (val: number) =>
  `₹${Number(val || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export interface NfaDetailsPageProps {
  recordId?: string;
  isDrawer?: boolean;
  onClose?: () => void;
}

export const NfaDetailsPage: React.FC<NfaDetailsPageProps> = ({
  recordId,
  isDrawer,
}) => {
  const { id: paramId } = useParams<{ id: string }>();
  const id = recordId || paramId;
  const navigate = useNavigate();

  const [nfa, setNfa] = useState<NoteForApproval | null>(null);
  const [request, setRequest] = useState<PurchaseRequest | null>(null);
  const [documents, setDocuments] = useState<DocumentAttachment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessingAction, setIsProcessingAction] = useState(false);

  // Modal Visibility States
  const [isApproveOpen, setIsApproveOpen] = useState(false);
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [isSendBackOpen, setIsSendBackOpen] = useState(false);
  const [isClarificationOpen, setIsClarificationOpen] = useState(false);
  const [isDelegateOpen, setIsDelegateOpen] = useState(false);

  const loadData = async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      const nfaData = await mockNfaLfaService.getNfaById(id);
      setNfa(nfaData);

      if (nfaData) {
        const [reqData, docs] = await Promise.all([
          mockPurchaseRequestService.getRequestById(nfaData.purchaseRequestId),
          mockDocumentCommentService.getDocuments('NFA', nfaData.id),
        ]);
        setRequest(reqData);
        setDocuments(docs);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  if (isLoading) {
    return (
      <div className="p-16 text-center text-xs text-foreground-secondary font-medium font-poppins">
        Loading Enterprise Note for Approval (NFA) Dossier...
      </div>
    );
  }

  if (!nfa) {
    return (
      <div className="p-16 text-center space-y-4 font-poppins">
        <h2 className="text-lg font-bold text-foreground">NFA Record Not Found</h2>
        <p className="text-xs text-foreground-secondary">
          The requested Note for Approval ID does not exist or has been archived.
        </p>
        <Button size="sm" onClick={() => navigate(ROUTES.NFA.LIST)} className="font-poppins cursor-pointer">
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to NFA Ledger
        </Button>
      </div>
    );
  }

  const currentStage =
    nfa.approvalChain.find((s) => s.status === 'InProgress') || nfa.approvalChain[0];
  const isActionable =
    nfa.status === 'UnderApproval' || nfa.status === 'ClarificationRequested';

  const handleApprove = async (comments: string) => {
    if (!currentStage) return;
    try {
      setIsProcessingAction(true);
      await mockNfaLfaService.approveStage({
        nfaId: nfa.id,
        stageId: currentStage.id,
        comments,
        version: nfa.version,
      });
      // Immediately cascade downstream PO and LFA creation
      await unifiedLifecycleCoordinator.onNfaApproved(nfa.id, comments);
      await loadData();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to approve NFA stage.');
    } finally {
      setIsProcessingAction(false);
    }
  };

  const handleReject = async (reason: string, comments: string) => {
    if (!currentStage) return;
    try {
      setIsProcessingAction(true);
      await mockNfaLfaService.rejectStage({
        nfaId: nfa.id,
        stageId: currentStage.id,
        reason,
        comments,
        version: nfa.version,
      });
      await loadData();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to reject NFA.');
    } finally {
      setIsProcessingAction(false);
    }
  };

  const handleSendBack = async (reason: string, targetStage: number) => {
    if (!currentStage) return;
    try {
      setIsProcessingAction(true);
      await mockNfaLfaService.sendBackStage({
        nfaId: nfa.id,
        stageId: currentStage.id,
        reason,
        targetStageNumber: targetStage,
        version: nfa.version,
      });
      await loadData();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to send back NFA.');
    } finally {
      setIsProcessingAction(false);
    }
  };

  const handleClarification = async (question: string, dueDate: string) => {
    try {
      setIsProcessingAction(true);
      await mockNfaLfaService.requestClarification({
        nfaId: nfa.id,
        question,
        dueDate,
      });
      await loadData();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to request clarification.');
    } finally {
      setIsProcessingAction(false);
    }
  };

  const handleDelegate = async (dto: {
    toUserId: string;
    toUserName: string;
    toUserRole: string;
    startDate: string;
    endDate: string;
    reason: string;
  }) => {
    try {
      setIsProcessingAction(true);
      await mockNfaLfaService.delegateApproval({
        nfaId: nfa.id,
        ...dto,
      });
      await loadData();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to delegate approval.');
    } finally {
      setIsProcessingAction(false);
    }
  };

  // Mock Budget calculation
  const totalBudget = 600000;
  const committedSpend = 420000;
  const thisNfaAmount = nfa.estimatedCost || 0;
  const remainingBudget = Math.max(0, totalBudget - (committedSpend + thisNfaAmount));
  const utilizationPct = Math.min(100, Math.round(((committedSpend + thisNfaAmount) / totalBudget) * 100));

  return (
    <div className={`space-y-4 font-poppins ${isDrawer ? 'pb-2' : 'pb-28'}`}>
      {/* 1. Header Navigation & Identification */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {!isDrawer && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(ROUTES.NFA.LIST)}
              className="rounded-xl border-border hover:bg-muted font-poppins cursor-pointer"
            >
              <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to NFA Ledger
            </Button>
          )}
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs font-bold text-primary px-2.5 py-1 bg-primary/10 rounded-lg border border-primary/20">
              {nfa.nfaNumber}
            </span>
            <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-muted text-foreground-secondary border border-border">
              {nfa.capexOpex}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <StatusBadge
            status={nfa.status}
            category="NFA"
            className="w-36 py-1 justify-center text-center font-bold text-xs rounded-full shadow-2xs font-poppins"
          />
        </div>
      </div>

      {/* Executive Committee Action Required Callout Banner */}
      {isActionable && (
        <div className="p-4 rounded-2xl bg-gradient-to-r from-amber-500/15 via-primary/10 to-emerald-500/15 border border-amber-500/30 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 font-poppins shadow-xs animate-in fade-in">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-amber-500/20 text-amber-700 dark:text-amber-300 shrink-0">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <div>
              <span className="font-extrabold text-sm text-foreground block">
                Executive Committee Signoff Pending
              </span>
              <span className="text-xs text-foreground-secondary">
                Your authorization releases the official Purchase Order (PO) and issues the Letter for Award (LFA).
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2.5 shrink-0">
            <Button
              size="sm"
              onClick={() => setIsApproveOpen(true)}
              className="rounded-xl font-extrabold bg-emerald-600 hover:bg-emerald-700 text-white shadow-md cursor-pointer flex items-center gap-1.5 transition-all hover:scale-105 active:scale-95 font-poppins"
            >
              <CheckCircle2 className="h-4 w-4" /> Authorize Commercial Signoff
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsRejectOpen(true)}
              className="rounded-xl font-bold text-rose-600 border-rose-200 hover:bg-rose-50 cursor-pointer font-poppins"
            >
              Reject
            </Button>
          </div>
        </div>
      )}

      {/* 2. Executive Overview & Justification Card */}
      <Card className="rounded-2xl border border-border bg-card p-6 shadow-xs space-y-5 font-poppins">
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4 border-b border-border/60 pb-5">
          <div className="space-y-1.5 max-w-3xl">
            <span className="text-[10px] font-bold uppercase tracking-wider text-primary font-poppins">
              Commercial Executive Dossier
            </span>
            <h1 className="text-xl sm:text-2xl font-extrabold text-foreground font-poppins tracking-tight">
              {nfa.title}
            </h1>
            <p className="text-xs text-foreground-secondary font-medium leading-relaxed font-poppins">
              {nfa.subject || 'Commercial authorization and technical evaluation review for procurement release.'}
            </p>
          </div>

          <div className="flex items-center gap-4 shrink-0 lg:text-right">
            <div className="p-3.5 rounded-xl bg-primary/5 border border-primary/15 text-left lg:text-right">
              <span className="text-[10px] font-bold uppercase tracking-wider text-foreground-muted block font-poppins">
                Proposed Commitment
              </span>
              <span className="text-xl sm:text-2xl font-black text-primary font-mono block">
                {formatInr(nfa.estimatedCost)}
              </span>
            </div>

            <div className="p-3.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-left lg:text-right">
              <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400 block font-poppins flex items-center gap-1 lg:justify-end">
                <TrendingUp className="h-3 w-3" /> Negotiated Savings
              </span>
              <span className="text-xl sm:text-2xl font-black text-emerald-600 dark:text-emerald-400 font-mono block">
                +{formatInr(nfa.savingsEstimated)}
              </span>
            </div>
          </div>
        </div>

        {/* Metadata Strip */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 pt-1 text-xs">
          <div>
            <span className="text-foreground-muted block text-[10px] font-semibold">Linked Requisition</span>
            <span
              onClick={() => navigate(ROUTES.REQUESTS.DETAILS(nfa.purchaseRequestId))}
              className="font-mono font-bold text-primary hover:underline cursor-pointer"
            >
              {nfa.requestNumber}
            </span>
          </div>

          <div>
            <span className="text-foreground-muted block text-[10px] font-semibold">Prepared By</span>
            <span className="font-semibold text-foreground truncate block">
              {nfa.preparedByName}
            </span>
            <span className="text-[10px] text-foreground-muted block truncate">
              {nfa.preparedByRole}
            </span>
          </div>

          <div>
            <span className="text-foreground-muted block text-[10px] font-semibold">Department</span>
            <span className="font-semibold text-foreground flex items-center gap-1 truncate">
              <Building2 className="h-3.5 w-3.5 text-primary shrink-0" />
              {nfa.departmentName}
            </span>
          </div>

          <div>
            <span className="text-foreground-muted block text-[10px] font-semibold">Plant / Cost Center</span>
            <span className="font-mono font-semibold text-foreground block truncate">
              {nfa.plantCode} • {nfa.costCenterCode}
            </span>
          </div>

          <div>
            <span className="text-foreground-muted block text-[10px] font-semibold">Expenditure Type</span>
            <span className="font-bold text-foreground flex items-center gap-1">
              <Tag className="h-3.5 w-3.5 text-primary shrink-0" />
              {nfa.capexOpex}
            </span>
          </div>

          <div>
            <span className="text-foreground-muted block text-[10px] font-semibold">Submission Date</span>
            <span className="font-semibold text-foreground flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5 text-foreground-muted shrink-0" />
              {new Date(nfa.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
            </span>
          </div>
        </div>

        {/* Embedded Operational Context & Commercial Justification */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-border/60 text-xs">
          <div className="p-3.5 rounded-xl bg-muted/30 border border-border/70 space-y-1">
            <span className="text-[10.5px] font-bold uppercase tracking-wider text-foreground block font-poppins">
              Operational Requirement Context
            </span>
            <p className="text-foreground-secondary italic leading-relaxed font-poppins">
              "{nfa.backgroundContext}"
            </p>
          </div>

          <div className="p-3.5 rounded-xl bg-muted/30 border border-border/70 space-y-1">
            <span className="text-[10.5px] font-bold uppercase tracking-wider text-foreground block font-poppins">
              Commercial Justification & Market Rate Benchmark
            </span>
            <p className="text-foreground-secondary italic leading-relaxed font-poppins">
              "{nfa.commercialJustification}"
            </p>
          </div>
        </div>
      </Card>

      {/* 3. Authorization Banner (If already Approved) */}
      {nfa.status === 'Approved' && (
        <div className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/25 flex items-center gap-3 font-poppins text-emerald-800 dark:text-emerald-300">
          <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
          <div className="text-xs">
            <span className="font-bold block text-sm">
              Note for Approval Authorized & Released for Purchase Order
            </span>
            <span className="text-[11px] text-emerald-700/80 dark:text-emerald-300/80">
              Commercial terms and cost-center allocations have been approved. Purchase Order generation is authorized.
            </span>
          </div>
        </div>
      )}

      {/* SLA Countdown Widget (Only if Pending) */}
      {isActionable && nfa.sla && (
        <SlaTrackerWidget sla={nfa.sla} />
      )}

      {/* Approval Stages Flow Visual Header */}
      <ApprovalStatusHeader stages={nfa.approvalChain} />

      {/* 4. Full-Width Procurement Line Items Ledger */}
      <Card className="rounded-2xl border border-border bg-card shadow-xs overflow-hidden font-poppins">
        <CardHeader className="p-4 border-b border-border/60 bg-muted/20">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-bold text-foreground flex items-center gap-2 font-poppins">
              <Layers className="h-4 w-4 text-primary" />
              Procurement Line Items & Pricing Breakdown
            </CardTitle>
            <span className="text-xs text-foreground-muted font-medium font-poppins">
              {request?.items?.length || 1} Item(s)
            </span>
          </div>
        </CardHeader>

        <CardContent className="p-0 overflow-x-hidden">
          <table className="w-full text-left text-xs font-poppins">
            <thead className="bg-muted/80 backdrop-blur-sm border-b border-border font-poppins">
              <tr>
                <th className="py-3 px-4 text-left font-bold text-black dark:text-white uppercase tracking-wider text-[11px]">
                  Item #
                </th>
                <th className="py-3 px-4 text-left font-bold text-black dark:text-white uppercase tracking-wider text-[11px]">
                  Description & Specifications
                </th>
                <th className="py-3 px-4 text-right font-bold text-black dark:text-white uppercase tracking-wider text-[11px]">
                  Qty / UOM
                </th>
                <th className="py-3 px-4 text-right font-bold text-black dark:text-white uppercase tracking-wider text-[11px]">
                  Est. Unit Price
                </th>
                <th className="py-3 px-4 text-right font-bold text-black dark:text-white uppercase tracking-wider text-[11px]">
                  Total Value
                </th>
                <th className="py-3 px-4 text-left font-bold text-black dark:text-white uppercase tracking-wider text-[11px]">
                  Plant / Cost Center
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {request?.items && request.items.length > 0 ? (
                request.items.map((item) => (
                  <tr key={item.id} className="hover:bg-muted/40 transition-colors">
                    <td className="py-3 px-4 font-mono font-semibold text-primary">{item.itemNumber}</td>
                    <td className="py-3 px-4">
                      <span className="font-bold text-foreground block">{item.materialName}</span>
                      {item.specifications && (
                        <span className="text-[10.5px] text-foreground-muted block truncate max-w-md">
                          {item.specifications}
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right font-mono font-semibold text-foreground">
                      {item.quantity} {item.unitOfMeasure}
                    </td>
                    <td className="py-3 px-4 text-right font-mono text-foreground-secondary">
                      {formatInr(item.estimatedUnitPrice)}
                    </td>
                    <td className="py-3 px-4 text-right font-mono font-bold text-foreground">
                      {formatInr(item.totalEstimatedPrice)}
                    </td>
                    <td className="py-3 px-4 font-mono text-[11px] text-foreground-secondary">
                      {item.plantCode || nfa.plantCode} • {item.costCenterCode || nfa.costCenterCode}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="py-3 px-4 font-mono text-primary font-semibold">10</td>
                  <td className="py-3 px-4 font-bold text-foreground">{nfa.title}</td>
                  <td className="py-3 px-4 text-right font-mono font-semibold">1 EA</td>
                  <td className="py-3 px-4 text-right font-mono text-foreground-secondary">
                    {formatInr(nfa.estimatedCost)}
                  </td>
                  <td className="py-3 px-4 text-right font-mono font-bold text-foreground">
                    {formatInr(nfa.estimatedCost)}
                  </td>
                  <td className="py-3 px-4 font-mono text-[11px] text-foreground-secondary">
                    {nfa.plantCode} • {nfa.costCenterCode}
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot className="border-t-2 border-border bg-muted/40 font-poppins">
              <tr>
                <td colSpan={4} className="py-3 px-4 text-right font-bold text-foreground">
                  Total Proposed Commercial Commitment:
                </td>
                <td className="py-3 px-4 text-right font-mono font-extrabold text-sm text-primary">
                  {formatInr(nfa.estimatedCost)}
                </td>
                <td className="py-3 px-4 text-right font-mono text-[11px] text-emerald-600 font-bold">
                  (Savings: +{formatInr(nfa.savingsEstimated)})
                </td>
              </tr>
            </tfoot>
          </table>
        </CardContent>
      </Card>

      {/* 5. Horizontal Cost Center Financial & Budget Clearance Bar */}
      <Card className="rounded-2xl border border-border bg-card p-5 shadow-xs font-poppins">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-border/60 pb-4">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            <div>
              <h3 className="font-bold text-sm text-foreground">Cost Center Financial Clearance</h3>
              <p className="text-[11px] text-foreground-muted">
                Budget line: <strong className="text-foreground">{nfa.costCenterCode}</strong> ({nfa.departmentName})
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4 text-xs font-poppins">
            <div className="text-right">
              <span className="text-[10px] text-foreground-muted block uppercase">Annual Limit</span>
              <span className="font-mono font-bold text-foreground">{formatInr(totalBudget)}</span>
            </div>
            <div className="text-right">
              <span className="text-[10px] text-foreground-muted block uppercase">Committed</span>
              <span className="font-mono font-bold text-foreground">{formatInr(committedSpend)}</span>
            </div>
            <div className="text-right">
              <span className="text-[10px] text-foreground-muted block uppercase">This NFA</span>
              <span className="font-mono font-bold text-primary">{formatInr(thisNfaAmount)}</span>
            </div>
            <div className="text-right">
              <span className="text-[10px] text-emerald-600 dark:text-emerald-400 block uppercase font-bold">
                Remaining Buffer
              </span>
              <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400">
                {formatInr(remainingBudget)}
              </span>
            </div>
          </div>
        </div>

        {/* Utilization Progress Bar */}
        <div className="pt-3 space-y-1.5">
          <div className="flex justify-between text-xs font-medium">
            <span className="text-foreground-secondary">
              Budget Utilization Trajectory: <strong className="text-foreground">{utilizationPct}% Allocated</strong>
            </span>
            <span className="font-mono text-foreground-muted text-[11px]">
              Available Headroom: {formatInr(remainingBudget)}
            </span>
          </div>
          <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-500 rounded-full ${
                utilizationPct > 90
                  ? 'bg-rose-500'
                  : utilizationPct > 75
                  ? 'bg-amber-500'
                  : 'bg-gradient-to-r from-blue-500 to-indigo-600'
              }`}
              style={{ width: `${utilizationPct}%` }}
            />
          </div>
        </div>
      </Card>

      {/* 6. Balanced 50/50 Lower Section: Supporting Documents & Approval Matrix */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 font-poppins">
        {/* Left (50%): Supporting Documents & Quotation Matrix */}
        <Card className="rounded-2xl border border-border bg-card shadow-xs font-poppins">
          <CardHeader className="p-4 border-b border-border/60">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-bold text-foreground flex items-center gap-2 font-poppins">
                <FileText className="h-4 w-4 text-primary" />
                Supporting Commercial Documentation
              </CardTitle>
              <span className="text-xs text-foreground-muted font-medium font-poppins">
                {documents.length || 2} File(s) Verified
              </span>
            </div>
          </CardHeader>
          <CardContent className="p-4 space-y-2.5">
            {documents.length > 0 ? (
              documents.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-3 rounded-xl border border-border bg-muted/20 hover:bg-muted/40 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0">
                      <FileText className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <span className="text-xs font-bold text-foreground truncate block font-poppins">
                        {doc.fileName}
                      </span>
                      <span className="text-[10px] text-foreground-muted font-mono block">
                        {(doc.fileSize / 1024).toFixed(1)} KB • {doc.category || 'Quotation'}
                      </span>
                    </div>
                  </div>

                  <a
                    href={doc.url || '#'}
                    target="_blank"
                    rel="noreferrer"
                    className="p-1.5 rounded-lg text-foreground-muted hover:text-primary hover:bg-muted transition-colors cursor-pointer"
                    title="Download / View"
                  >
                    <Download className="h-4 w-4" />
                  </a>
                </div>
              ))
            ) : (
              <>
                <div className="flex items-center justify-between p-3 rounded-xl border border-border bg-muted/20">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0">
                      <FileText className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <span className="text-xs font-bold text-foreground truncate block font-poppins">
                        Competitive_Quotation_Scoring_Matrix.pdf
                      </span>
                      <span className="text-[10px] text-foreground-muted font-mono block">
                        248.5 KB • Supplier Evaluation
                      </span>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 cursor-pointer">
                    <Download className="h-4 w-4" />
                  </Button>
                </div>

                <div className="flex items-center justify-between p-3 rounded-xl border border-border bg-muted/20">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0">
                      <FileText className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <span className="text-xs font-bold text-foreground truncate block font-poppins">
                        Technical_Specifications_And_Scope.docx
                      </span>
                      <span className="text-[10px] text-foreground-muted font-mono block">
                        512.0 KB • Technical Specs
                      </span>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 cursor-pointer">
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Right (50%): Sequential Governance & Approval Chain */}
        <Card className="rounded-2xl border border-border bg-card shadow-xs font-poppins">
          <CardHeader className="p-4 border-b border-border/60">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-bold text-foreground flex items-center gap-2 font-poppins">
                <ShieldCheck className="h-4 w-4 text-primary" />
                Delegation of Authority (DOA) Governance Chain
              </CardTitle>
              <span className="text-[10px] font-mono font-semibold px-2 py-0.5 rounded bg-muted text-foreground-secondary border border-border">
                {nfa.approvalMode} Mode
              </span>
            </div>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            {nfa.approvalChain.map((stage, idx) => {
              const isApproved = stage.status === 'Approved';
              const isInProgress = stage.status === 'InProgress';
              const isRejected = stage.status === 'Rejected';

              return (
                <div
                  key={stage.id}
                  className={`p-3 rounded-xl border transition-all ${
                    isInProgress
                      ? 'border-primary/40 bg-primary/5 ring-1 ring-primary/20'
                      : isApproved
                      ? 'border-emerald-500/25 bg-emerald-500/5'
                      : isRejected
                      ? 'border-rose-500/25 bg-rose-500/5'
                      : 'border-border bg-muted/10'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2.5">
                      <div
                        className={`h-6 w-6 rounded-full flex items-center justify-center font-bold text-[11px] shrink-0 ${
                          isApproved
                            ? 'bg-emerald-600 text-white'
                            : isInProgress
                            ? 'bg-primary text-primary-foreground'
                            : isRejected
                            ? 'bg-rose-600 text-white'
                            : 'bg-muted text-foreground-muted'
                        }`}
                      >
                        {isApproved ? '✓' : idx + 1}
                      </div>
                      <div>
                        <span className="font-bold text-xs text-foreground block font-poppins">
                          {stage.stageName} — {stage.approverRole}
                        </span>
                        <span className="text-[11px] text-foreground-secondary block">
                          {stage.approverName} {stage.approverEmail ? `(${stage.approverEmail})` : ''}
                        </span>
                      </div>
                    </div>

                    <span
                      className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider shrink-0 ${
                        isApproved
                          ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                          : isInProgress
                          ? 'bg-primary/15 text-primary'
                          : isRejected
                          ? 'bg-rose-500/15 text-rose-700 dark:text-rose-300'
                          : 'bg-muted text-foreground-muted'
                      }`}
                    >
                      {stage.status}
                    </span>
                  </div>

                  {stage.comments && (
                    <p className="mt-2 text-[11px] text-foreground-secondary italic bg-card/60 p-2 rounded-lg border border-border/50">
                      "{stage.comments}"
                    </p>
                  )}
                  {stage.actionDate && (
                    <span className="text-[10px] text-foreground-muted mt-1 block font-mono">
                      Timestamp: {new Date(stage.actionDate).toLocaleString('en-IN')}
                    </span>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* 7. Sticky Bottom Action Bar (Only if Actionable) */}
      {isActionable && (
        <ApprovalActionBar
          isActionable={isActionable}
          onOpenApproveModal={() => setIsApproveOpen(true)}
          onOpenRejectModal={() => setIsRejectOpen(true)}
          onOpenSendBackModal={() => setIsSendBackOpen(true)}
          onOpenClarificationModal={() => setIsClarificationOpen(true)}
          onOpenDelegateModal={() => setIsDelegateOpen(true)}
          isProcessing={isProcessingAction}
        />
      )}

      {/* Action Modals (React createPortal mounted to document.body) */}
      <ApproveModal
        isOpen={isApproveOpen}
        onClose={() => setIsApproveOpen(false)}
        nfaNumber={nfa.nfaNumber}
        amount={nfa.estimatedCost}
        onConfirm={handleApprove}
      />

      <RejectModal
        isOpen={isRejectOpen}
        onClose={() => setIsRejectOpen(false)}
        nfaNumber={nfa.nfaNumber}
        onConfirm={handleReject}
      />

      <SendBackModal
        isOpen={isSendBackOpen}
        onClose={() => setIsSendBackOpen(false)}
        nfaNumber={nfa.nfaNumber}
        onConfirm={handleSendBack}
      />

      <ClarificationModal
        isOpen={isClarificationOpen}
        onClose={() => setIsClarificationOpen(false)}
        nfaNumber={nfa.nfaNumber}
        onConfirm={handleClarification}
      />

      <DelegateModal
        isOpen={isDelegateOpen}
        onClose={() => setIsDelegateOpen(false)}
        nfaNumber={nfa.nfaNumber}
        onConfirm={handleDelegate}
      />
    </div>
  );
};
